import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import FETCHER from "./_fetcher";
import {
  IAddCart,
  IAddRedemptionToCart,
  ICartItem,
  IGetCartResponse,
  ICartCheckoutResponse,
} from "types/cart";
import { IUser } from "types/user";
import { RedemptionType } from "types/redemption";
import {
  trackAddToCart,
  trackEvent,
  trackRemoveFromCart,
  trackUpdateCart,
} from "../utils/analytics";

const CART_QUERY_KEYS = {
  TOTAL_ITEMS: "getCartTotalItems",
  TOTAL_VALUE: "getCartTotalValue",
  CART: "getCart",
} as const;

export const addToCart = async ({
  productId,
  quantity,
  redemptionItemId,
  redemptionType,
}: IAddCart) => {
  // Determine the endpoint based on whether it's a regular product or redemption item
  const endpoint = redemptionItemId ? "/cart/redemption" : "/cart/add";

  const payload = redemptionItemId
    ? {
        redemptionItemId,
        redemptionType,
        quantity,
      }
    : {
        productId,
        quantity,
      };

  const result = await FETCHER.post(endpoint, payload);
  return result;
};

export const useAddToCart = () => {
  const queryClient = useQueryClient();

  const { data, mutateAsync, isPending } = useMutation({
    mutationFn: addToCart,
    onSuccess: (response, variables) => {
      // Track the add to cart event
      if (response?.data) {
        const { productId, quantity, redemptionItemId, redemptionType } =
          variables;
        const responseData = response.data;

        if (redemptionItemId) {
          // Track redemption item add to cart
          trackEvent("Add Redemption Item to Cart", {
            redemption_item_id: redemptionItemId,
            redemption_type: redemptionType,
            item_name: responseData.redemptionItem?.name || "Redemption Item",
            points_cost: responseData.redemptionItem?.pointsCost || 0,
            cash_cost: responseData.redemptionItem?.cashCost || 0,
            quantity: quantity,
            timestamp: new Date().toISOString(),
          });
        } else if (productId) {
          // Track regular product add to cart
          trackAddToCart(
            productId,
            responseData.productName || "Product",
            responseData.price || 0,
            quantity,
            responseData.category
          );
        }
      }

      // Invalidate all cart-related queries
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_ITEMS ||
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_VALUE ||
          query.queryKey[0] === CART_QUERY_KEYS.CART,
      });
    },
    onError: (error, variables) => {
      // Track the failed add to cart event with Mixpanel
      const { productId, quantity, redemptionItemId } = variables;

      trackEvent("Add to Cart Failed", {
        product_id: productId,
        redemption_item_id: redemptionItemId,
        quantity: quantity,
        timestamp: new Date().toISOString(),
        error_message: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });
  return {
    data: data,
    addToCartAsync: mutateAsync,
    isPending,
  };
};

// Dedicated function for adding redemption items to cart
export const addRedemptionToCart = async ({
  redemptionItemId,
  redemptionType,
  quantity = 1,
}: IAddRedemptionToCart) => {
  const result = await FETCHER.post("/cart/redemption", {
    redemptionItemId,
    redemptionType,
    quantity,
  });
  return result;
};

export const useAddRedemptionToCart = () => {
  const queryClient = useQueryClient();

  const { data, mutateAsync, isPending } = useMutation({
    mutationFn: addRedemptionToCart,
    onSuccess: (response, variables) => {
      // Track the add redemption to cart event
      if (response?.data) {
        const { redemptionItemId, redemptionType, quantity } = variables;
        const responseData = response.data;

        // Find the added redemption item in the response
        const addedItem = responseData.cartItems?.find(
          (item: ICartItem) => item.redemptionItemId === redemptionItemId
        );

        if (addedItem && addedItem.redemptionItem) {
          trackEvent("Add Redemption Item to Cart", {
            redemption_item_id: redemptionItemId,
            redemption_type: redemptionType,
            item_name: addedItem.redemptionItem.name,
            quantity: quantity,
            points_cost: addedItem.redemptionItem.pointsCost,
            cash_cost: addedItem.redemptionItem.cashCost,
            timestamp: new Date().toISOString(),
          });
        }
      }

      // Invalidate all cart-related queries
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_ITEMS ||
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_VALUE ||
          query.queryKey[0] === CART_QUERY_KEYS.CART,
      });
    },
    onError: (error, variables) => {
      // Track the error event
      trackEvent("Add Redemption Item to Cart Failed", {
        redemption_item_id: variables.redemptionItemId,
        redemption_type: variables.redemptionType,
        quantity: variables.quantity,
        timestamp: new Date().toISOString(),
        error_message: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });

  return {
    data: data,
    addRedemptionToCartAsync: mutateAsync,
    isPending,
  };
};

export const updateItemInCart = async (
  cartItemId: number,
  quantity: number
) => {
  const result = await FETCHER.patch(`/cart/item/${cartItemId}`, {
    quantity,
  });
  return result;
};

// Update redemption item quantity in cart using cart item ID
export const updateRedemptionItemInCart = async (
  cartItemId: number,
  quantity: number
) => {
  const result = await FETCHER.patch(`/cart/item/${cartItemId}`, {
    quantity,
  });
  return result;
};

export const useUpdateItemInCart = () => {
  const queryClient = useQueryClient();
  // For service hooks, we'll use a simpler approach without user dependency
  const { cartItemList } = useGetCart();

  const { data, mutateAsync, isPending } = useMutation({
    mutationFn: ({
      cartItemId,
      quantity,
    }: {
      cartItemId: number;
      quantity: number;
    }) => updateItemInCart(cartItemId, quantity),
    onSuccess: (_, variables) => {
      // Track the update cart event
      if (cartItemList && variables) {
        const { cartItemId, quantity } = variables;
        const cartItem = cartItemList.find((item) => item.id === cartItemId);

        if (cartItem && cartItem.productPrice) {
          // Track the cart update event with Mixpanel
          trackUpdateCart(
            cartItem.productPrice.productId,
            cartItem.productPrice.product.name,
            parseFloat(cartItem.productPrice.price),
            cartItem.quantity, // old quantity
            quantity // new quantity
          );
        }
      }

      // Invalidate all cart-related queries
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_ITEMS ||
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_VALUE ||
          query.queryKey[0] === CART_QUERY_KEYS.CART,
      });
    },
  });
  return {
    data: data,
    updateItemInCartAsync: mutateAsync,
    isPending,
  };
};

export const useUpdateRedemptionItemInCart = () => {
  const queryClient = useQueryClient();
  const { cartItemList } = useGetCart();

  const { data, mutateAsync, isPending } = useMutation({
    mutationFn: ({
      cartItemId,
      quantity,
    }: {
      cartItemId: number;
      quantity: number;
    }) => updateRedemptionItemInCart(cartItemId, quantity),
    onSuccess: (_, variables) => {
      // Track the update cart event for redemption items
      if (cartItemList && variables) {
        const { cartItemId, quantity } = variables;
        const cartItem = cartItemList.find((item) => item.id === cartItemId);

        if (cartItem && cartItem.redemptionItem) {
          // Track the redemption item update event
          trackEvent("Update Redemption Item in Cart", {
            cart_item_id: cartItemId,
            redemption_item_id: cartItem.redemptionItemId,
            item_name: cartItem.redemptionItem.name,
            old_quantity: cartItem.quantity,
            new_quantity: quantity,
            points_cost: cartItem.redemptionItem.pointsCost,
            cash_cost: cartItem.redemptionItem.cashCost,
            timestamp: new Date().toISOString(),
          });
        }
      }

      // Invalidate all cart-related queries
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_ITEMS ||
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_VALUE ||
          query.queryKey[0] === CART_QUERY_KEYS.CART,
      });
    },
  });
  return {
    data: data,
    updateRedemptionItemInCartAsync: mutateAsync,
    isPending,
  };
};
export const getCart = async () => {
  try {
    const result = await FETCHER.get<IGetCartResponse>(`/cart`);
    return result.data;
  } catch (e) {
    console.log(e);
  }
};
export const useGetCart = (user?: IUser) => {
  const { data, isLoading } = useQuery({
    queryKey: ["getCart"],
    queryFn: () => getCart(),
    enabled: !!user, // Only enable the query if user exists
  });

  return {
    cartItemList: data?.cartItems,
    totalItemRedemptionDiscount: data?.totalItemRedemptionDiscount || 0,
    isLoading,
  };
};

// Enhanced cart for checkout with redemption details
export const getCartForCheckout = async (): Promise<
  ICartCheckoutResponse | undefined
> => {
  try {
    const result = await FETCHER.get<ICartCheckoutResponse>(`/cart/checkout`);
    return result.data;
  } catch (e) {
    console.log(e);
  }
};

export const useGetCartForCheckout = (user?: IUser) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["getCartForCheckout"],
    queryFn: () => getCartForCheckout(),
    enabled: !!user,
  });

  return {
    cartData: data,
    cartItemList: data?.cartItems,
    totalItemRedemptionDiscount: data?.totalItemRedemptionDiscount || 0,
    isLoading,
    refetch,
  };
};

// Apply redemption to existing cart item
export const applyRedemptionToCartItem = async (
  cartItemId: number,
  redemptionItemId: number,
  redemptionType: RedemptionType
) => {
  const result = await FETCHER.post(`/cart/item/${cartItemId}/redemption`, {
    redemptionType,
    redemptionItemId,
  });
  return result;
};

export const useApplyRedemptionToCartItem = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      cartItemId,
      redemptionItemId,
      redemptionType,
    }: {
      cartItemId: number;
      redemptionItemId: number;
      redemptionType: RedemptionType;
    }) =>
      applyRedemptionToCartItem(cartItemId, redemptionItemId, redemptionType),
    onSuccess: () => {
      // Invalidate cart queries
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === CART_QUERY_KEYS.CART ||
          query.queryKey[0] === "getCartForCheckout",
      });
    },
  });

  return {
    applyRedemptionToCartItemAsync: mutateAsync,
    isPending,
  };
};

export const getCartTotalItems = async () => {
  try {
  } catch (e) {
    console.log(e);
  }
};
export const useGetCartTotalItem = (user?: IUser) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["getCartTotalItems"],
    queryFn: () => getCartTotalItems(),
    enabled: !!user,
  });
  if (!user) {
    return { data: 0, isLoading: false, refetch };
  }
  return {
    totalItems: data?.totalItems || 0,
    isLoading,
    refetchTotalItems: refetch,
  };
};

export const getCartTotalValue = async () => {
  try {
    const result = await FETCHER.get<number>(`/cart/total`);
    return result.data;
  } catch (e) {
    console.log(e);
  }
};
export const useGetCartTotalValue = () => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["getCartTotalValue"],
    queryFn: () => getCartTotalValue(),
    retry: false,
  });
  return {
    totalValue: data || 0,
    isLoading,
    refetchTotalValue: refetch,
  };
};

export const deleteItemInCart = async (id: number) => {
  const result = await FETCHER.delete(`/cart/item/${id}`);
  return result;
};

// Bulk delete multiple cart items
export const bulkDeleteItemsInCart = async (itemIds: number[]) => {
  // Execute all delete operations in parallel
  const deletePromises = itemIds.map((id) =>
    FETCHER.delete(`/cart/item/${id}`)
  );
  const results = await Promise.all(deletePromises);
  return results;
};

export const useDeleteItemInCart = () => {
  const queryClient = useQueryClient();
  const { cartItemList } = useGetCart();

  const { data, mutateAsync, isPending } = useMutation({
    mutationFn: deleteItemInCart,
    onSuccess: (_, productId) => {
      // Track the remove from cart event
      if (cartItemList) {
        // In this case, productId is the cart item ID, not the product ID
        const cartItem = cartItemList.find((item) => item.id === productId);

        if (cartItem && cartItem.productPrice) {
          // Track the remove from cart event with Mixpanel
          trackRemoveFromCart(
            cartItem.productPrice.productId,
            cartItem.productPrice.product.name,
            parseFloat(cartItem.productPrice.price),
            cartItem.quantity
          );
        }
      }

      // Invalidate all cart-related queries
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_ITEMS ||
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_VALUE ||
          query.queryKey[0] === CART_QUERY_KEYS.CART,
      });
    },
  });
  return {
    data: data,
    deleteItemInCartAsync: mutateAsync,
    isPending,
  };
};

export const useBulkDeleteItemsInCart = () => {
  const queryClient = useQueryClient();
  const { cartItemList } = useGetCart();

  const { data, mutateAsync, isPending } = useMutation({
    mutationFn: bulkDeleteItemsInCart,
    onSuccess: (_, itemIds) => {
      // Track the bulk remove from cart event
      if (cartItemList) {
        itemIds.forEach((itemId) => {
          const cartItem = cartItemList.find((item) => item.id === itemId);
          if (cartItem) {
            if (cartItem.redemptionItem) {
              // Track redemption item removal
              trackEvent("Remove Redemption Item from Cart", {
                cart_item_id: itemId,
                redemption_item_id: cartItem.redemptionItemId,
                item_name: cartItem.redemptionItem.name,
                quantity: cartItem.quantity,
                points_cost: cartItem.redemptionItem.pointsCost,
                cash_cost: cartItem.redemptionItem.cashCost,
                timestamp: new Date().toISOString(),
              });
            } else if (cartItem.productPrice) {
              // Track regular product removal
              trackRemoveFromCart(
                cartItem.productPrice.productId,
                cartItem.productPrice.product.name,
                parseFloat(cartItem.productPrice.price),
                cartItem.quantity
              );
            }
          }
        });

        // Track bulk operation
        trackEvent("Bulk Remove from Cart", {
          items_count: itemIds.length,
          item_ids: itemIds,
          timestamp: new Date().toISOString(),
        });
      }

      // Invalidate all cart-related queries
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_ITEMS ||
          query.queryKey[0] === CART_QUERY_KEYS.TOTAL_VALUE ||
          query.queryKey[0] === CART_QUERY_KEYS.CART,
      });
    },
  });
  return {
    data: data,
    bulkDeleteItemsInCartAsync: mutateAsync,
    isPending,
  };
};
